import { Component } from '@theme/component';
import { ThemeEvents, PackUpdateEvent } from '@theme/events';

/**
 * @typedef {Object} PackSelectorRefs
 * @property {HTMLElement} quantityDisplay
 * @property {HTMLElement} priceEach
 * @property {HTMLInputElement[]} inputs
 */

/**
 * @extends {Component<PackSelectorRefs>}
 */
class PackSelectorComponent extends Component {
  requiredRefs = ['quantityDisplay', 'priceEach', 'inputs'];

  /** @type {AbortController} */
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });

    this.#updateActiveButton();
    this.#syncQuantityToForm();
    this.#updatePriceFromSelection();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * @param {Event} event
   */
  selectPack(event) {
    if (!(event.target instanceof HTMLInputElement)) return;

    const value = parseInt(event.target.value, 10);
    if (isNaN(value)) return;

    this.refs.quantityDisplay.textContent = value.toString();
    this.#updateActiveButton();
    this.#syncQuantityToForm();
    this.#updatePriceFromSelection();
  }

  #updateActiveButton() {
    for (const input of this.refs.inputs) {
      const label = input.closest('.pack-selector__button');
      if (!label) continue;
      label.classList.toggle('pack-selector__button--selected', input.checked);
      label.classList.toggle('is-checked', input.checked);
    }
  }

  #syncQuantityToForm() {
    const checkedInput = this.refs.inputs.find((input) => input.checked);
    if (!checkedInput) return;

    const quantity = checkedInput.value;
    const section = this.closest('.shopify-section');
    if (!section) return;

    const formInput = /** @type {HTMLInputElement | null} */ (
      section.querySelector('form[data-type="add-to-cart-form"] input[name="quantity"]')
    );
    if (formInput) {
      formInput.value = quantity;
    }

    const productForm = /** @type {any} */ (section.querySelector('product-form-component'));
    if (productForm) {
      productForm.dataset.quantityDefault = quantity;
    }
  }

  #updatePriceFromSelection() {
    const checkedInput = this.refs.inputs.find((input) => input.checked);
    if (!checkedInput) return;

    const perItemPrice = checkedInput.dataset.perItemPrice;
    if (perItemPrice) {
      this.refs.priceEach.textContent = `| ${perItemPrice} each`;
    }

    const quantity = parseInt(checkedInput.value, 10);
    const unitPrice = parseInt(this.dataset.unitPrice || '0', 10);
    const labelEl = checkedInput.closest('.pack-selector__button')?.querySelector('.pack-selector__label');
    const packLabel = labelEl?.textContent?.trim() || '';

    this.dispatchEvent(
      new PackUpdateEvent({
        quantity,
        perItemPriceFormatted: perItemPrice || '',
        unitPriceCents: unitPrice,
        packLabel,
      })
    );
  }

  /**
   * @param {Event} event
   */
  #onVariantUpdate = (event) => {
    const detail = /** @type {any} */ (event).detail;
    if (!detail?.resource) return;

    const newPrice = detail.resource.price;
    if (newPrice) {
      this.dataset.unitPrice = newPrice.toString();
    }

    requestAnimationFrame(() => this.#updatePriceFromSelection());
  };
}

if (!customElements.get('pack-selector-component')) {
  customElements.define('pack-selector-component', PackSelectorComponent);
}
